import React, { createContext, useContext, useCallback, useEffect, useState, useRef, ReactNode } from 'react';
import { notificationsApi, Notification } from 'api/notifications';

// Enhanced types
export interface NotificationState {
  unreadCount: number;
  notifications: Notification[];
  loading: boolean;
  apiAvailable: boolean;
  error: string | null;
  lastFetched: Date | null;
}

export interface NotificationContextType extends NotificationState {
  // Actions
  fetchNotificationCount: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  triggerNotiRefresh: () => void;
  clearError: () => void;
  
  // Convenience methods
  refreshCount: () => void;
  refreshNotifications: () => void;
}

// Create context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Provider component
interface NotificationProviderProps {
  children: ReactNode;
  enablePolling?: boolean;
  pollingInterval?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  enablePolling = true, 
  pollingInterval = 30000 
}) => {
  // Global state - shared across all components
  const [state, setState] = useState<NotificationState>({
    unreadCount: 0,
    notifications: [],
    loading: false,
    apiAvailable: true,
    error: null,
    lastFetched: null,
  });

  // Refs for managing async operations
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Fetch notification count
  const fetchNotificationCount = useCallback(async () => {
    if (!state.apiAvailable) return;

    try {
      setState(prev => ({ ...prev, error: null }));
      const data = await notificationsApi.getCount();
      const count = data?.count ?? 0;
      
      setState(prev => ({
        ...prev,
        unreadCount: count,
        lastFetched: new Date(),
      }));
    } catch (error: any) {
      console.error("Failed to fetch notification count:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch notifications";
      
      setState(prev => ({
        ...prev,
        error: errorMessage,
        unreadCount: 0,
      }));

      // Disable polling if API returns 404 or 501 (not implemented)
      if (error?.response?.status === 404 || error?.response?.status === 501) {
        setState(prev => ({ ...prev, apiAvailable: false }));
      }
    }
  }, [state.apiAvailable]);

  // Fetch notifications list
  const fetchNotifications = useCallback(async () => {
    if (!state.apiAvailable) return;

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await notificationsApi.getAll();
      const notifications = result?.items || [];
      
      setState(prev => ({
        ...prev,
        notifications,
        loading: false,
        lastFetched: new Date(),
      }));
    } catch (error: any) {
      console.error("Failed to fetch notifications:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch notifications";
      
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false,
        notifications: [],
      }));

      if (error?.response?.status === 404 || error?.response?.status === 501) {
        setState(prev => ({ ...prev, apiAvailable: false }));
      }
    }
  }, [state.apiAvailable]);

  // Mark notification as read with optimistic updates
  const markAsRead = useCallback(async (id: string) => {
    const notification = state.notifications.find(n => n.id === id);
    if (!notification || notification.notification_status === 'READ') return;

    // Optimistic update
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(notif =>
        notif.id === id
          ? {
              ...notif,
              notification_status: "READ" as const,
              read_at: new Date().toISOString(),
            }
          : notif
      ),
      unreadCount: Math.max(0, prev.unreadCount - 1),
    }));

    try {
      await notificationsApi.markAsRead(id);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      
      // Revert optimistic update on error
      if (notification) {
        setState(prev => ({
          ...prev,
          notifications: prev.notifications.map(notif => (notif.id === id ? notification : notif)),
          unreadCount: prev.unreadCount + 1,
        }));
      }
    }
  }, [state.notifications, state.unreadCount]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const unreadNotifications = state.notifications.filter(n => n.notification_status === 'UNREAD');
    
    // Optimistic update
    const now = new Date().toISOString();
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(notif => ({
        ...notif,
        notification_status: "READ" as const,
        read_at: now,
      })),
      unreadCount: 0,
    }));

    try {
      await notificationsApi.markAllAsRead();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      
      // Revert optimistic update on error
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(notif => {
          const original = unreadNotifications.find(u => u.id === notif.id);
          return original || notif;
        }),
        unreadCount: unreadNotifications.length,
      }));
    }
  }, [state.notifications]);

  // Trigger immediate refresh with debouncing
  const triggerNotiRefresh = useCallback(() => {
    // Clear existing timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce the refresh to prevent excessive API calls
    debounceTimerRef.current = setTimeout(() => {
      fetchNotificationCount();
      window.dispatchEvent(new CustomEvent('notification-refresh'));
    }, 300);
  }, [fetchNotificationCount]);

  // Trigger refresh for notifications list
  const triggerNotificationsRefresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Clear error state
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Convenience methods
  const refreshCount = triggerNotiRefresh;
  const refreshNotifications = triggerNotificationsRefresh;

  // Initialize and setup polling
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Initial fetch
    fetchNotificationCount();
    
    if (enablePolling) {
      intervalRef.current = setInterval(fetchNotificationCount, pollingInterval);
    }
    
    // Listen for manual refresh events
    const handleManualRefresh = () => {
      fetchNotificationCount();
    };
    window.addEventListener('notification-refresh', handleManualRefresh);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('notification-refresh', handleManualRefresh);
      
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchNotificationCount, enablePolling, pollingInterval]);

  const contextValue: NotificationContextType = {
    // State
    ...state,
    
    // Actions
    fetchNotificationCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    triggerNotiRefresh,
    clearError,
    
    // Convenience methods
    refreshCount,
    refreshNotifications,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the context
export const useNotificationContext = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};

// Simple hook for triggering refreshes (for components that don't need full state)
export const useNotificationRefresh = () => {
  const triggerNotiRefresh = useCallback(() => {
    window.dispatchEvent(new CustomEvent('notification-refresh'));
  }, []);

  return { triggerNotiRefresh };
};
